import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  AlertBanner,
  Checkbox,
  DateAndTimePicker,
  Icon,
  InlineMessage,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../utils/utils';
import CertsModalCommentBox from '../../CertsModalCommentBox';
import { BULK_ACTION_ENDDATE } from '../mockData';

import { COMMENT_MINIMUM_LENGTH, getBulkActionFooterBtns } from './constant';

function BulkActionEndDateBody({
  bulkActionEndDate,
  setBulkActionEndDate,
  handleCloseBukActionEndDateAlert,
  bulkPredefinedComments,
}) {
  const intl = useIntl();
  const {
    core: {
      configMap: { ConditionallyCertifyInDaysLimit, ExtendEndDateDaysLimit },
      campaignType,
    },
    global: { globalDateFormat },
    lineActions: { certificationStartDate, reassignConfigMap },
  } = useSelector((state) => state?.certifications?.details);
  const { predefinedComments: { allowEditPredefinedComments = false } = {} } =
    useSelector((state) => state?.certifications?.details?.core || {});
  const { messages } = useSelector((state) => state?.user);
  const [minAndMaxDateToExtend, setMinAndMaxDateToExtend] = useState({
    minDate: new Date(),
    maxDate: new Date(
      new Date().setFullYear(new Date(certificationStartDate).getFullYear() + 1)
    ),
  });

  useEffect(() => {
    if (
      bulkActionEndDate?.id ===
        getBulkActionFooterBtns(campaignType, reassignConfigMap)
          .CONDITIONAL_CERTIFIY.id &&
      Number(ConditionallyCertifyInDaysLimit) !== 0
    ) {
      const certStartDate = new Date(certificationStartDate);

      let getAllowedMaxDays;

      if (Number(ConditionallyCertifyInDaysLimit) > 1) {
        getAllowedMaxDays = Number(ConditionallyCertifyInDaysLimit) - 1;
      } else {
        getAllowedMaxDays = 0;
      }

      const maxDate = new Date(
        certStartDate.setDate(certStartDate.getDate() + getAllowedMaxDays)
      );

      setMinAndMaxDateToExtend((prevDateConfig) => ({
        ...prevDateConfig,
        maxDate,
      }));
    }

    if (
      bulkActionEndDate?.id ===
        getBulkActionFooterBtns(campaignType, reassignConfigMap).EXTEND_END_DATE
          .id &&
      Number(ExtendEndDateDaysLimit) !== 0
    ) {
      const certStartDate = new Date(certificationStartDate);

      let getAllowedMaxDays;

      if (Number(ExtendEndDateDaysLimit) > 1) {
        getAllowedMaxDays = Number(ExtendEndDateDaysLimit) - 1;
      } else {
        getAllowedMaxDays = 0;
      }

      const maxDate = new Date(
        certStartDate.setDate(certStartDate.getDate() + getAllowedMaxDays)
      );

      setMinAndMaxDateToExtend((prevDateConfig) => ({
        ...prevDateConfig,
        maxDate,
      }));
    }
  }, [
    bulkActionEndDate?.isOpen,
    certificationStartDate,
    ConditionallyCertifyInDaysLimit,
    ExtendEndDateDaysLimit,
  ]);

  const handleCommentDescription = (text) => {
    setBulkActionEndDate((prev) => ({
      ...prev,
      comment: text,
    }));
  };

  const handleBulkActionDate = (date) => {
    setBulkActionEndDate((prev) => ({
      ...prev,
      endDate: date ? new Date(date) : null,
    }));
  };

  const handleHasAgreedChange = () => {
    setBulkActionEndDate((prev) => ({
      ...prev,
      hasAgreed: !prev.hasAgreed,
    }));
  };

  const isAlertVisible = Boolean(
    bulkActionEndDate?.showAlert && !bulkActionEndDate?.isSubmitting
  );

  const commonBodyClass = classnames(
    'BulkActionEndDateBody',
    isAlertVisible && 'BulkActionEndDateBody-alertBox--1'
  );

  return (
    <>
      <AlertBanner
        colorTheme='Critical'
        title={
          messages['Required.fields.missing.ATTESTATIONS'] ||
          localize(intl, getMessageKey('Required.fields.missing'))
        }
        description=''
        isVisible={
          bulkActionEndDate?.showAlert && !bulkActionEndDate?.isSubmitting
        }
        isColonVisible={false}
        onCancel={handleCloseBukActionEndDateAlert}
        isFloating
        position={{
          type: 'absolute',
          top: '0',
        }}
      />
      <div className={commonBodyClass}>
        <DateAndTimePicker
          label={
            messages.CERT_BULK_CED_NEW_DATE ||
            localize(intl, getMessageKey('CERT_BULK_CED_NEW_DATE'))
          }
          isRequired
          isDisabled={bulkActionEndDate?.isSubmitting}
          isCritical={
            bulkActionEndDate?.showAlert && !bulkActionEndDate?.endDate
          }
          setIsCritical={() => {}}
          CriticalHelperText={
            <InlineMessage kind='missingRequiredField' size='small' />
          }
          dateFormat={globalDateFormat}
          placeholderText={globalDateFormat?.toUpperCase()}
          isOverflowVisible
          startDate={bulkActionEndDate?.endDate}
          setStartDate={handleBulkActionDate}
          maxDate={minAndMaxDateToExtend?.maxDate}
          minDate={minAndMaxDateToExtend?.minDate}
        />
        <CertsModalCommentBox
          defaultComments={bulkPredefinedComments}
          isDisabled={bulkActionEndDate?.isSubmitting}
          showAlert={
            bulkActionEndDate?.showAlert &&
            bulkActionEndDate?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
              COMMENT_MINIMUM_LENGTH
          }
          onChange={handleCommentDescription}
          clearModal={bulkActionEndDate?.isOpen}
          label={messages.comments || localize(intl, getMessageKey('comments'))}
          allowEditPredefinedComments={allowEditPredefinedComments}
        />
        <div className='BulkActionsBody-checkBoxDiv'>
          <Checkbox
            id='bulk-action-end-date-checkbox'
            className='BulkActionBody-endDateCheckbox'
            name='checkbox-error'
            kind='singleLine'
            dataTestId='checkbox-error'
            isDisabled={bulkActionEndDate?.isSubmitting}
            isChecked={bulkActionEndDate?.hasAgreed}
            isRequired={!bulkActionEndDate?.hasAgreed}
            isCritical={
              bulkActionEndDate?.showAlert && !bulkActionEndDate?.hasAgreed
            }
            onChange={handleHasAgreedChange}
            label={
              messages['BULKACTION.confirm.message.ATTESTATIONS'] ||
              localize(intl, getMessageKey('BULKACTION.confirm.message'))
            }
            textPosition='after'
            HelperText={
              bulkActionEndDate?.showAlert &&
              !bulkActionEndDate?.hasAgreed && (
                <InlineMessage
                  text={
                    messages['Required.field.ATTESTATIONS'] ||
                    localize(intl, getMessageKey('Required.field'))
                  }
                  colorTheme='critical'
                  size='small'
                  leftIcon={<Icon kind='AlertCriticalFilled' />}
                />
              )
            }
          />
        </div>
      </div>
    </>
  );
}

BulkActionEndDateBody.propTypes = {
  bulkActionEndDate: PropTypes.shape(BULK_ACTION_ENDDATE).isRequired,
  setBulkActionEndDate: PropTypes.func.isRequired,
  handleCloseBukActionEndDateAlert: PropTypes.func.isRequired,
  bulkPredefinedComments: PropTypes.arrayOf(PropTypes.string),
};

BulkActionEndDateBody.defaultProps = {
  bulkPredefinedComments: [],
};

export default BulkActionEndDateBody;
